import React, { forwardRef, useImperativeHandle, useRef, useEffect } from "react";

/* The input bar.
 *
 * Treated as the primary target on the page: raised off the background, and it
 * visibly responds to focus so there is never doubt about where typing goes.
 */

const CopilotComposer = forwardRef(
  ({ value, onChange, onSend, onStop, streaming, stopping, disabled, onKeyDownCapture, above,
     onPickProject, projectPickerOpen }, ref) => {
  const areaRef = useRef(null);
  useImperativeHandle(ref, () => ({ focus: () => areaRef.current?.focus() }));

  /* The buttons sit on mb-1, not mb-0.5.
   *
   * The row is items-end so they stay put as the textarea grows, which means
   * their offset from the bottom has to match the text's own. A single line is
   * 14.5px x leading-relaxed (23.56px) plus py-2, so the box is 39.56px tall —
   * the min-h-[36px] never binds — and its centre is 19.78px up. A 32px button
   * on mb-0.5 centres at 18px, leaving every icon sitting ~2px low against the
   * text it is meant to line up with. mb-1 lands within a quarter pixel.
   */

  // Grow with the content up to a ceiling, then scroll. A fixed-height box
  // hides the start of anything longer than a sentence, which is most of what
  // people ask a copilot.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [value]);

  const canSend = value.trim().length > 0 && !disabled;
  /* The square means "stop"; the arrow means "send". Which one is right depends
   * on whether there is anything to send.
   *
   * Work can be running while the composer is still usable — a proposal writes
   * for minutes and the user is free to type through it. Showing only a stop
   * button then left a typed message with no way out but Enter, and a button
   * that would have thrown the message away. With a draft present, sending IS
   * the action, and it ends the run on its way past. */
  const showStop = streaming && !value.trim();

  return (
    // Same max-width as the header and the turns: one spine down the page. The
    // gradient lets content scroll up and dissolve under the composer instead of
    // colliding with a hard edge.
    // pb-7 rather than pb-4: removing the disclaimer line took its spacing with
    // it, leaving the box sitting on the bottom edge of the window.
    //
    // from-[62%] rather than via-white: `via` puts the midpoint at full white,
    // so the bottom HALF of this block was opaque and the fade was squeezed
    // into the top half — which read as a hard white bar sitting under the
    // conversation, most visible behind the context chip. Now the solid part
    // stops just below the input and everything above it genuinely fades out.
    // Marked so the side panel's click-outside does not treat typing a message
    // as "I am done with the panel". Everything else outside it does close it.
    <div data-sensa-keep-panel className="pointer-events-auto shrink-0 bg-gradient-to-t from-white from-[62%] to-transparent px-6 pb-7 pt-4">
      {/* The slash menu and the context chips render here, inside the composer's
          own column, so they stay locked to the input's left edge at any width. */}
      {above}
      <div className="mx-auto w-full max-w-[860px]">
        <div className="group/composer flex items-end gap-2 rounded-2xl border border-[#A04A97]/15 bg-white/95 p-1.5 pl-2.5 shadow-[0_1px_2px_rgba(24,16,28,0.04),0_12px_30px_-18px_rgba(24,16,28,0.45)] backdrop-blur-[10px] transition-[border-color,box-shadow] duration-200 hover:border-[#A04A97]/30 focus-within:border-[#A04A97]/55 focus-within:shadow-[0_0_0_3px_rgba(160,74,151,0.12),0_16px_36px_-20px_rgba(160,74,151,0.55)] motion-reduce:transition-none">
          {/* Was an upload glyph with no click handler at all — it promised a
              thing the composer has never done, since documents are added in the
              project panel. A "+" promises nothing specific, which is exactly
              right for the one control that opens "what can I attach", and it
              replaces the "@" trigger nobody could see. */}
          <button
            type="button"
            onClick={onPickProject}
            title="Attach a project"
            aria-label="Attach a project"
            aria-expanded={!!projectPickerOpen}
            aria-haspopup="listbox"
            className={`mb-1 flex h-8 w-8 flex-none items-center justify-center rounded-[10px] transition-[background-color,color,transform] duration-200 ease-sidebar hover:-translate-y-[1px] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
              projectPickerOpen
                ? "bg-[#A04A97]/12 text-[#A04A97]"
                : "text-[#77707E] hover:bg-[#A04A97]/10 hover:text-[#A04A97]"
            }`}
          >
            <svg
              aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"
              /* Rotates into an x when open, so the same target closes it. */
              className={`transition-transform duration-200 ease-sidebar motion-reduce:transition-none ${
                projectPickerOpen ? "rotate-45" : ""
              }`}
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>

          <textarea
            ref={areaRef}
            rows={1}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            // Enter sends, Shift+Enter breaks the line — the convention every
            // chat tool shares, so it needs no explaining.
            onKeyDown={(e) => {
              // The slash menu owns arrows and Enter while it is open, so both
              // don't act on the same keystroke — Enter would otherwise send the
              // literal "/project" text instead of picking the highlighted row.
              if (onKeyDownCapture?.(e)) {
                e.preventDefault();
                return;
              }
              if (e.key === "Escape" && streaming) {
                e.preventDefault();
                onStop?.();
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSend) onSend();
              }
            }}
            placeholder="Ask anything"
            className="max-h-[168px] min-h-[36px] flex-1 resize-none border-0 bg-transparent py-2 text-[15.5px] font-normal leading-relaxed text-[#18101C] outline-none placeholder:font-normal placeholder:text-[#77707E] disabled:cursor-not-allowed"
          />

          {/* One button, two jobs. Sending and stopping are the same slot in
              the same place because they are the same decision at two moments —
              a separate stop control would sit dead for most of the session and
              move the send target when it appeared. */}
          {showStop ? (
            <button
              type="button"
              onClick={onStop}
              disabled={stopping}
              title="Stop generating"
              aria-label="Stop generating"
              className="mb-1 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#A04A97] text-white transition-[background-color,transform,opacity] duration-200 ease-sidebar hover:bg-[#8B3F83] active:scale-95 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              {/* A square, not an X: X reads as dismiss/close, and people expect
                  a stop to keep what has already been written. */}
              <span className="h-[9px] w-[9px] rounded-[2px] bg-current" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              title="Send"
              className={`mb-1 flex h-8 w-8 flex-none items-center justify-center rounded-full transition-[background-color,color,transform] duration-200 ease-sidebar focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none ${
                canSend
                  ? "bg-[#A04A97] text-white hover:bg-[#8B3F83] active:scale-95"
                  : "cursor-not-allowed bg-[#18101C]/[0.05] text-[#77707E]"
              }`}
            >
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
  }
);

CopilotComposer.displayName = "CopilotComposer";

export default CopilotComposer;
